/**
 * Datos que se refrescan sin recargar
 * -----------------------------------------------------------------------------
 * ⚠ REGLA DE LA SUITE: **todo lo que se pinta desde la base de datos tiene que
 * poder actualizarse sin recargar la página.** Un título, el nombre de la
 * empresa en la barra, un contador, un estado: si el dato cambia en el servidor
 * y la pantalla sigue enseñando el viejo, la aplicación está mintiendo.
 *
 * Pasa más de lo que parece porque el HTML se pinta una vez, al cargar. El
 * usuario cambia la razón social en otra pestaña, vuelve a esta y la barra
 * superior sigue con el nombre anterior hasta que alguien pulsa F5. Nadie
 * reporta eso como un fallo; simplemente deja de fiarse de lo que ve.
 *
 * El TRANSPORTE lo pone la aplicación —WebSocket, sondeo, Turbo, Live
 * Components—: eso depende de con qué esté construida y el theme no se mete.
 * Lo que sí pone el theme es la CONVENCIÓN, para que todas las apps marquen el
 * dato igual y el refresco sea una línea:
 *
 *   <h1 data-company-live="empresa.nombre">Owari S.A.S.</h1>
 *   <span data-company-live="pedidos.pendientes">12</span>
 *   <img data-company-live="empresa.logo" data-company-live-attr="src" src="…">
 *
 *   CompanyTheme.updateLive({ 'empresa.nombre': 'Owari SAS', 'pedidos.pendientes': 13 });
 *
 * Marcar cuesta un atributo; no marcar cuesta enterarse tarde de que media
 * pantalla está desactualizada y no saber cuáles.
 */

const ATTR = 'data-company-live';
const ATTR_TARGET = 'data-company-live-attr';

/**
 * Actualiza en el documento todo lo marcado con las claves que se pasen.
 *
 * Solo toca lo que cambia: escribir el mismo texto reiniciaría una selección
 * del usuario y dispararía animaciones sin motivo.
 *
 * @param {Object} values `{ 'empresa.nombre': 'Owari SAS' }`
 * @param {ParentNode} [root=document]
 * @returns {number} Cuántos elementos se actualizaron.
 */
export function updateLive(values = {}, root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return 0;

  let cambiados = 0;

  Object.keys(values).forEach((key) => {
    const value = values[key];

    root.querySelectorAll(`[${ATTR}="${key}"]`).forEach((node) => {
      // Por defecto se cambia el texto; con `-attr` se cambia un atributo, que
      // es lo que hace falta para un `src`, un `href` o un `title`.
      const target = node.getAttribute(ATTR_TARGET);

      if (target) {
        if (node.getAttribute(target) === String(value)) return;
        node.setAttribute(target, value);
      } else {
        if (node.textContent === String(value)) return;
        node.textContent = value;
      }

      cambiados += 1;

      // Un cambio silencioso en pantalla pasa desapercibido. La clase dura lo
      // que la animación y no deja rastro.
      node.classList.remove('company-live-changed');
      void node.offsetWidth; // reinicia la animación si vuelve a cambiar
      node.classList.add('company-live-changed');

      node.dispatchEvent(
        new CustomEvent('changed.company.live', { bubbles: true, detail: { key, value } })
      );
    });
  });

  return cambiados;
}

/**
 * Las claves marcadas en el documento. Sirve para pedirle al servidor solo lo
 * que esta pantalla enseña, en vez de traer el registro entero.
 *
 * @param {ParentNode} [root=document]
 * @returns {string[]}
 */
export function liveKeys(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];

  const keys = new Set();
  root.querySelectorAll(`[${ATTR}]`).forEach((node) => keys.add(node.getAttribute(ATTR)));

  return [...keys];
}

export default updateLive;
